/**
 * CORE-BR — Weather Operational Intelligence · advisory hook
 *
 * Fetches the shop's current reading together with the server-side
 * assessment of it against the shop's own policy, and hands surfaces a
 * per-bay advisory.
 *
 * The evaluation is not done here. `get-weather` performs it, because the
 * shop's thresholds mean one thing and only one evaluator should decide what
 * that is. A second copy in the browser would drift from the one that writes
 * `weather_alerts`, and then the board and the alert list would disagree
 * about the same shop's rule.
 *
 * Nothing in this hook can block work. It returns descriptions.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  advisoriesForBays,
  summariseAssessment,
  type AdvisoryBay,
  type BayWeatherAdvisory,
  type OutdoorWorkAssessment,
} from '@/lib/weather/outdoorAdvisory';

/** How often the reading is refreshed. Matches the weather pill's cadence. */
const REFRESH_MS = 10 * 60 * 1000;

/**
 * What we show before the first response arrives.
 *
 * `unavailable`, not `clear`: until we have actually looked, we have not
 * looked. The distinction is the whole point of this capability's state
 * model, and a loading placeholder that quietly meant "fine" would
 * reintroduce the defect at the very first render.
 */
const NOT_YET_READ: OutdoorWorkAssessment = {
  state: 'unavailable',
  reason: 'reading_missing',
  explanation:
    'Weather has not been checked yet. This is not a statement that conditions are acceptable.',
};

export interface UseOutdoorWeatherAdvisoryResult {
  assessment: OutdoorWorkAssessment;
  /** One entry per bay passed in, in the same order. Never filtered. */
  advisories: BayWeatherAdvisory[];
  /** Shop-level line, or null when there is nothing to say. */
  summary: { level: string; message: string } | null;
  isLoading: boolean;
  refetch: () => void;
}

export function useOutdoorWeatherAdvisory(
  bays: ReadonlyArray<AdvisoryBay>,
): UseOutdoorWeatherAdvisoryResult {
  const { activeShop } = useAuth();
  const [assessment, setAssessment] =
    useState<OutdoorWorkAssessment>(NOT_YET_READ);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAssessment = useCallback(async () => {
    if (!activeShop?.id) {
      setAssessment({
        state: 'unavailable',
        reason: 'location_missing',
        explanation:
          'Weather could not be checked because no shop is selected.',
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-weather', {
        body: { shop_id: activeShop.id, evaluate_outdoor_policy: true },
      });

      if (error) throw error;

      // The server sends the assessment alongside the reading. If it is
      // absent for any reason we say so rather than inferring anything.
      if (data?.outdoor_assessment) {
        setAssessment(data.outdoor_assessment as OutdoorWorkAssessment);
      } else {
        setAssessment({
          state: 'unavailable',
          reason: 'reading_missing',
          explanation:
            'Weather could not be verified — no assessment was returned. This is not a statement that conditions are acceptable.',
        });
      }
    } catch {
      // A failed call is uncertainty, never permission and never a block.
      setAssessment({
        state: 'unavailable',
        reason: 'provider_failed',
        explanation:
          'Weather could not be verified — the weather service could not be reached. This is not a statement that conditions are acceptable.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [activeShop?.id]);

  useEffect(() => {
    fetchAssessment();
    const interval = setInterval(fetchAssessment, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchAssessment]);

  const advisories = useMemo(
    () => advisoriesForBays(bays ?? [], assessment),
    [bays, assessment],
  );

  const summary = useMemo(() => summariseAssessment(assessment), [assessment]);

  return { assessment, advisories, summary, isLoading, refetch: fetchAssessment };
}
